import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import ReferralLabel from 'marketing/ReferralLabel'
import TextButton from 'LuxKit/Button/TextButton'

const NON_PENDING_LOG_STATUS = ['redeemed', 'order_ineligible']

const Wrapper = styled.div`
  margin-top: ${rem(36)};

  & > * + * {
    box-shadow: ${props => `0px 1px 0px 0px ${props.theme.palette.neutral.default.six} inset`};
  }

  & .showOnHighWidth {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    & .hiddenOnHighWidth {
      display: none;
    }

    & .showOnHighWidth {
      display: block;
    }
  }
`

const Row = styled.div`
  &.heading{
    display: none;
  }

  ${mediaQueryUp.tablet} {
    width: 100%;
    display: grid;
    grid-auto-columns: 1fr;
    grid-auto-flow: column;
    align-items: center;

    &.heading{
      display: grid;
    }

    & > * {
      overflow: hidden;
    }
  }
`

const Cell = styled.div`
  padding: ${rem(8)};

  &.flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  & .align-text{
    text-align: right;
  }

  & > .first-name-text {
    display: none;
  }

  & .pending-reward {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    &.flex {
      display: block;

      > :first-child{
        display: none;
      }
    }

    & .align-text{
      text-align: left;
    }

    & > .first-name-text {
      display: block;
    }

    & > .first-name-heading {
      display: none;
    }
  }
`

interface Props {
  referralLogs: Array<App.ReferralLogItem>
}

function ReferralGrid({ referralLogs }: Props) {
  const intl = useIntl()

  return (
    <div>
      <Heading variant="heading3">
        {intl.formatMessage({
          id: 'tMY6iTLEfNDGZ7FDYdMo',
          defaultMessage: 'Your reward activity',
          description: 'Section heading for the referral reward activity section',
        })}
      </Heading>
      <Wrapper>
        <Row className="heading">
          <Cell>
            <Heading variant="heading6">
              {intl.formatMessage({
                id: '48QmkEkPnv5QJGIubaou',
                defaultMessage: 'Referral',
                description: 'Column heading for referral name in reward activity grid',
              })}
            </Heading>
          </Cell>
          <Cell>
            <Heading variant="heading6">
              {intl.formatMessage({
                id: 'zOUUwbzJPTKmzflXRYTe',
                defaultMessage: 'Status',
                description: 'Column heading for referral status in reward activity grid',
              })}
            </Heading>
          </Cell>
          <Cell>
            <Heading variant="heading6">
              {intl.formatMessage({
                id: '6I19cY5V55WY_rrNZQFm',
                defaultMessage: 'Reward',
                description: 'Column heading for referral reward in reward activity grid',
              })}
            </Heading>
          </Cell>
        </Row>
        {referralLogs.map((log, index) => <Row data-testid="referral-logs" key={`${log.first_name}${index}`}>
          <Cell>
            <BodyText variant="medium" weight="bold" className="first-name-text">{log.first_name}</BodyText>
            <Heading variant="heading6" className="first-name-heading">{log.first_name}</Heading>
          </Cell>
          <Cell className="flex">
            <BodyText variant="medium" weight="bold">
              <FormattedMessage
                id="iAzLMH0Di7k8tkIozNLq"
                defaultMessage="Status"
                description="Column header for referral status on the marketing page"
              />
            </BodyText>
            <ReferralLabel referralLabel={log.log_status} />
          </Cell>
          <Cell className="flex">
            <BodyText variant="medium" weight="bold" className={clsx({ 'pending-reward': log.log_status !== 'redeemed' })}>
              <FormattedMessage
                id="qB70yA2Amm0eOuCZLV_J"
                defaultMessage="Reward"
                description="Column header for referral reward on the marketing page"
              />
            </BodyText>
            {!NON_PENDING_LOG_STATUS.includes(log.log_status) && <TextButton kind="secondary" data-testid="pending-button" fit="full-width" disabled>
              {intl.formatMessage({
                id: 'mNknjeC_9Tvl3qeFXlhj',
                defaultMessage: 'Pending',
                description: 'Button text indicating a referral reward is pending',
              })}
            </TextButton>}
            {log.log_status === 'redeemed' && (
              <span>
                <BodyText data-testid="log-earn-amount" variant="medium" weight="bold" className="align-text">{log.earn_amount}</BodyText>
                <Caption variant="medium" className="align-text" colour="neutral-three">{log.earn_details}</Caption>
              </span>
            )}
          </Cell>
        </Row>)}
      </Wrapper>
    </div>
  )
}

export default ReferralGrid
